import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, FileSignature, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle, Callout, Checkbox, ColorInput,
  FormField, IconButton, Input, Label, Radio, SegmentedControl, Textarea,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import PdfLivePreview from "./PdfLivePreview";
import SettingsSection from "./SettingsSection";

const FONT_OPTIONS = [
  { value: "sans", label: "Modern", description: "A clean sans-serif. Easy to read on a phone." },
  { value: "serif", label: "Classic", description: "A formal serif, like a printed contract." },
];

const DISPLAY_OPTIONS = [
  { value: "none", label: "Neither" },
  { value: "notes", label: "Notes" },
  { value: "terms", label: "Terms & conditions" },
  { value: "both", label: "Both" },
];

const SECTION_OPTIONS = [
  { key: "employment", label: "Previous employment", description: "The companies a student worked at." },
  { key: "salary", label: "Indicative salary and job preferences", description: "CTC ranges, locations, work mode, notice period." },
  { key: "program", label: "Placement programme", description: "The recommended programme and its plan." },
  { key: "charges", label: "Programme charges", description: "The amount, inside the placement programme." },
];

/** The list of points under a heading: type, reorder, remove, add. */
function PointsEditor({ name, items, onChange, limits, numbered = false }) {
  const update = (i, value) => onChange(items.map((item, j) => (j === i ? value : item)));
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const move = (i, by) => {
    const next = [...items];
    const [row] = next.splice(i, 1);
    next.splice(i + by, 0, row);
    onChange(next);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {items.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key -- a row IS its position
        <div key={i} className="tw-grid tw-grid-cols-[1.25rem_minmax(0,1fr)] tw-items-start tw-gap-x-2 tw-gap-y-1 md:tw-grid-cols-[1.25rem_minmax(0,1fr)_auto]">
          <span className="tw-pt-2 tw-text-right tw-text-[0.8125rem] tw-font-semibold tw-tabular-nums tw-text-content-subtle" aria-hidden="true">
            {numbered ? `${i + 1}.` : "•"}
          </span>
          <Textarea
            rows={2}
            value={item}
            maxLength={limits.item}
            aria-label={`${name}, point ${i + 1}`}
            onChange={(e) => update(i, e.target.value)}
          />
          <div className="tw-col-start-2 tw-flex tw-items-center tw-justify-end tw-gap-0.5 md:tw-col-start-3">
            <IconButton label={`Move point ${i + 1} up`} icon={ArrowUp} size="sm" disabled={i === 0} onClick={() => move(i, -1)} />
            <IconButton label={`Move point ${i + 1} down`} icon={ArrowDown} size="sm" disabled={i === items.length - 1} onClick={() => move(i, 1)} />
            <IconButton label={`Remove point ${i + 1}`} icon={Trash2} size="sm" variant="ghost-destructive" onClick={() => remove(i)} />
          </div>
        </div>
      ))}
      <AppButton
        size="sm"
        variant="outline"
        className="tw-self-start"
        disabled={items.length >= limits.items}
        onClick={() => onChange([...items, ""])}
      >
        <Plus size={16} /> Add a point
      </AppButton>
    </div>
  );
}

/** A paragraph that is the standard wording until someone rewrites it. */
function WordingField({ label, value, standard, limit, onChange }) {
  return (
    <FormField
      label={label}
      hint={value ? `${value.length} / ${limit}` : "Using the standard wording."}
    >
      <Textarea rows={4} value={value} maxLength={limit} placeholder={standard} onChange={(e) => onChange(e.target.value)} />
    </FormField>
  );
}

/**
 * Settings → Evaluation & Agreement — what a placement agreement says and how
 * it looks: its title, font, colour and logo, which blocks it prints, the
 * institute's evaluation notes and terms & conditions (either, both or neither),
 * the wording of its declarations, and who usually signs.
 *
 * One form, saved with its button. The preview beside it is the real agreement
 * PDF for a sample student, drawn by the server from the unsaved form. A save
 * reaches agreements not sent yet; one already sent keeps the wording it went
 * out with.
 *
 * Rules: vc_be/utils/agreementTemplate. A blank wording field IS the standard
 * wording, so the placeholder shows what will print.
 */
export default function AgreementSettings({ showToast }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState(false);

  const adopt = useCallback((payload) => {
    setData(payload);
    setForm(payload.template);
    setSaved(payload.template);
  }, []);

  useEffect(() => {
    let live = true;
    apiClient.get("/student-profile/agreement-template")
      .then(({ data: payload }) => { if (live) adopt(payload); })
      .catch(() => { if (live) setLoadError(true); });
    return () => { live = false; };
  }, [adopt]);

  const dirty = useMemo(() => Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved)), [form, saved]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (loadError) return <Callout tone="danger">Couldn&apos;t load your agreement settings. Please reload the page.</Callout>;
  if (!form) return <Shimmer height={52} count={6} />;

  const { defaults, limits, variables, brand } = data;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setList = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const showNotes = form.display === "notes" || form.display === "both";
  const showTerms = form.display === "terms" || form.display === "both";
  const hasWords = (list) => list.items.some((i) => i.trim());
  const emptyList = (showNotes && !hasWords(form.notes)) || (showTerms && !hasWords(form.terms));
  const colourMissing = form.accent === "custom" && !/^#[0-9a-f]{6}$/i.test(form.accentColor);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data: payload } = await apiClient.put("/student-profile/agreement-template", form);
      adopt(payload);
      showToast?.("Agreement settings saved. Agreements you send from now on follow them.");
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't save your agreement settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <FileSignature size={18} aria-hidden="true" /> Evaluation &amp; Agreement
          </span>
        </AppCardTitle>
        <AppCardDescription>
          What your placement agreement says and how it looks. Changes reach agreements you have not sent yet; one already
          sent keeps the wording it went out with.
        </AppCardDescription>
      </AppCardHeader>

      <AppCardContent>
        <div className="tw-grid tw-gap-8 xl:tw-grid-cols-[minmax(0,1fr)_minmax(0,30rem)]">
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-5">
            <SettingsSection title="Evaluation notes and terms" description="Printed on every agreement, above the student's acknowledgement.">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <Label>Show on the agreement</Label>
                <SegmentedControl
                  label="Show on the agreement"
                  options={DISPLAY_OPTIONS}
                  value={form.display}
                  onChange={(display) => set({ display })}
                />
              </div>

              {showNotes && (
                <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-4">
                  <FormField label="Heading">
                    <Input
                      value={form.notes.title}
                      maxLength={limits.heading}
                      placeholder={defaults.notes.title}
                      onChange={(e) => setList("notes", { title: e.target.value })}
                    />
                  </FormField>
                  <PointsEditor
                    name={form.notes.title || defaults.notes.title}
                    items={form.notes.items}
                    limits={limits}
                    onChange={(items) => setList("notes", { items })}
                  />
                </div>
              )}

              {showTerms && (
                <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-4">
                  <FormField label="Heading">
                    <Input
                      value={form.terms.title}
                      maxLength={limits.heading}
                      placeholder={defaults.terms.title}
                      onChange={(e) => setList("terms", { title: e.target.value })}
                    />
                  </FormField>
                  <PointsEditor
                    name={form.terms.title || defaults.terms.title}
                    items={form.terms.items}
                    limits={limits}
                    numbered
                    onChange={(items) => setList("terms", { items })}
                  />
                </div>
              )}

              {emptyList && <Callout tone="info">A list with no points is left off the agreement.</Callout>}
            </SettingsSection>

            <SettingsSection title="Title and look">
              <FormField label="Document title">
                <Input
                  value={form.title}
                  maxLength={limits.title}
                  placeholder={defaults.title}
                  onChange={(e) => set({ title: e.target.value })}
                />
              </FormField>

              <div className="tw-flex tw-flex-col tw-gap-2">
                <Label>Font</Label>
                <div className="tw-grid tw-gap-3 md:tw-grid-cols-2" role="radiogroup" aria-label="Font">
                  {FONT_OPTIONS.map((o) => (
                    <Radio
                      key={o.value}
                      variant="card"
                      name="agreement-font"
                      value={o.value}
                      checked={form.fontStyle === o.value}
                      onChange={() => set({ fontStyle: o.value })}
                      label={o.label}
                      description={o.description}
                    />
                  ))}
                </div>
              </div>

              <div className="tw-flex tw-flex-col tw-gap-2">
                <Label>Accent colour</Label>
                <div className="tw-grid tw-gap-3 md:tw-grid-cols-2" role="radiogroup" aria-label="Accent colour">
                  <Radio
                    variant="card"
                    name="agreement-accent"
                    value="brand"
                    checked={form.accent === "brand"}
                    onChange={() => set({ accent: "brand" })}
                    label="Your brand colour"
                    description="Follows your institute's theme."
                  />
                  <Radio
                    variant="card"
                    name="agreement-accent"
                    value="custom"
                    checked={form.accent === "custom"}
                    onChange={() => set({ accent: "custom", accentColor: form.accentColor || brand.accent })}
                    label="A colour for agreements"
                    description="Used on headings and the top bar."
                  />
                </div>
                {form.accent === "custom" && (
                  <ColorInput
                    value={form.accentColor}
                    fallback={brand.accent}
                    aria-label="Accent colour for agreements"
                    onChange={(accentColor) => set({ accentColor })}
                  />
                )}
              </div>

              <Checkbox
                checked={form.showLogo && brand.hasLogo}
                disabled={!brand.hasLogo}
                onChange={(e) => set({ showLogo: e.target.checked })}
                label="Show your logo"
                description={brand.hasLogo ? "Your logo from Settings → Branding, at the top of the first page." : "Upload a logo in Settings → Branding first."}
              />
            </SettingsSection>

            <SettingsSection title="What it includes" description="Student details, the recommendation, the acknowledgement and the signatures are always printed.">
              {SECTION_OPTIONS.map((o) => (
                <Checkbox
                  key={o.key}
                  checked={form.sections[o.key]}
                  disabled={o.key === "charges" && !form.sections.program}
                  onChange={(e) => set({ sections: { ...form.sections, [o.key]: e.target.checked } })}
                  label={o.label}
                  description={o.description}
                />
              ))}
            </SettingsSection>

            <SettingsSection
              title="Wording"
              description="Leave a box empty to use the standard wording, shown faintly inside it. Your own words apply to every agreement you send."
            >
              <p className="tw-m-0 tw-text-xs tw-text-content-muted">
                These fill themselves in, here and in your notes and terms:{" "}
                {variables.map((v, i) => (
                  <React.Fragment key={v.key}>
                    {i > 0 && ", "}
                    <code className="tw-font-mono tw-text-[0.75rem] tw-text-content">{`{{${v.key}}}`}</code>
                  </React.Fragment>
                ))}
              </p>
              <WordingField
                label="Opening paragraph"
                value={form.introduction}
                standard={defaults.introduction}
                limit={limits.paragraph}
                onChange={(introduction) => set({ introduction })}
              />
              <WordingField
                label="The student's acknowledgement"
                value={form.studentDeclaration}
                standard={defaults.studentDeclaration}
                limit={limits.paragraph}
                onChange={(studentDeclaration) => set({ studentDeclaration })}
              />
              <WordingField
                label="The institute's undertaking"
                value={form.instituteDeclaration}
                standard={defaults.instituteDeclaration}
                limit={limits.paragraph}
                onChange={(instituteDeclaration) => set({ instituteDeclaration })}
              />
            </SettingsSection>

            <SettingsSection title="Signature and footer">
              <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                <FormField label="Who usually signs" hint="Filled in when you countersign. You can change it each time.">
                  <Input
                    value={form.signatoryName}
                    maxLength={limits.name}
                    placeholder="Priya Nair"
                    onChange={(e) => set({ signatoryName: e.target.value })}
                  />
                </FormField>
                <FormField label="Their designation">
                  <Input
                    value={form.signatoryTitle}
                    maxLength={limits.name}
                    placeholder="Director — Placements"
                    onChange={(e) => set({ signatoryTitle: e.target.value })}
                  />
                </FormField>
              </div>
              <FormField label="Footer line" hint="On every page. Empty prints your institute's name.">
                <Input
                  value={form.footerNote}
                  maxLength={limits.footer}
                  placeholder="Your company registration or a short address"
                  onChange={(e) => set({ footerNote: e.target.value })}
                />
              </FormField>
            </SettingsSection>

            {error && <Callout tone="danger" role="alert">{error}</Callout>}

            <div className="tw-sticky tw-bottom-0 tw-z-10 tw--mx-1 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-bg-surface-raised tw-px-1 tw-py-3">
              <AppButton onClick={save} disabled={!dirty || colourMissing || saving}>
                <Save size={16} /> {saving ? "Saving…" : "Save agreement settings"}
              </AppButton>
              {dirty && (
                <AppButton variant="ghost" onClick={() => adopt(data)} disabled={saving}>
                  <RotateCcw size={14} /> Discard changes
                </AppButton>
              )}
              <span className="tw-text-xs tw-text-content-muted" aria-live="polite">
                {dirty ? "You have unsaved changes." : "All changes saved."}
              </span>
            </div>
          </div>

          <div className="tw-min-w-0 xl:tw-sticky xl:tw-top-4 xl:tw-self-start">
            <PdfLivePreview
              url="/student-profile/agreement-template/sample"
              body={form}
              heading="Preview — the real PDF, for a sample student"
              label="Sample agreement preview"
              fileName="sample-agreement.pdf"
              footnote="The same document your students read and sign."
            />
          </div>
        </div>
      </AppCardContent>
    </AppCard>
  );
}
